import React, { useState, useEffect } from 'react';
import { useDogs } from '../hooks/useDogs';
import { APPOINTMENT_STATUS } from '../utils/vetUtils';

const EMPTY_FORM = {
    fullName: '', phone: '', dogName: '', address: '',
    cause: '', vetNotes: '', dogId: '',
    scheduledDate: '', scheduledTime: '',
};

// The two halves of a stored appointment date, in the local timezone the
// <input type="date"> / <input type="time"> pair expects.
const dateInputValue = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const timeInputValue = (value) => {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function WalkInVetForm({ onSubmit, onCancel, editingAppointment, loading }) {
    const [mode, setMode] = useState('walkin');
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [dogSearch, setDogSearch] = useState('');
    const { dogs, loadDogs } = useDogs();

    // Search dogs against the full dataset (not just the first page) as the user types
    useEffect(() => {
        const timer = setTimeout(() => {
            loadDogs({ search: dogSearch, limit: 50 });
        }, 300);
        return () => clearTimeout(timer);
    }, [dogSearch, loadDogs]);

    useEffect(() => {
        if (!editingAppointment) { setForm(EMPTY_FORM); setMode('walkin'); return; }

        // Only a visit booked for later fills the scheduling inputs. One that is
        // already in progress (or finished) is dated by when it actually
        // happened, and empty inputs are what leave that date alone on save.
        const booked = editingAppointment.status === APPOINTMENT_STATUS.SCHEDULED
            && editingAppointment.appointmentDate;
        const scheduling = {
            scheduledDate: booked ? dateInputValue(editingAppointment.appointmentDate) : '',
            scheduledTime: booked ? timeInputValue(editingAppointment.appointmentDate) : '',
        };

        if (editingAppointment.isWalkIn) {
            setMode('walkin');
            setForm({
                fullName: editingAppointment.walkInInfo?.fullName || '',
                phone:    editingAppointment.walkInInfo?.phone    || '',
                dogName:  editingAppointment.walkInInfo?.dogName  || '',
                address:  editingAppointment.walkInInfo?.address  || '',
                cause:    editingAppointment.cause    || '',
                vetNotes: editingAppointment.vetNotes || '',
                dogId: '',
                ...scheduling,
            });
        } else {
            setMode('registered');
            setForm({
                ...EMPTY_FORM,
                cause:    editingAppointment.cause    || '',
                vetNotes: editingAppointment.vetNotes || '',
                dogId:    editingAppointment.dog?._id || '',
                ...scheduling,
            });
        }
    }, [editingAppointment]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const errs = {};
        if (mode === 'walkin') {
            if (!form.fullName.trim()) errs.fullName = 'Nombre requerido';
            if (!form.phone.trim())    errs.phone    = 'Teléfono requerido';
            if (!form.dogName.trim())  errs.dogName  = 'Nombre del perro requerido';
        } else {
            if (!form.dogId) errs.dogId = 'Selecciona un perro';
        }
        if (!form.cause.trim()) errs.cause = 'Motivo de visita requerido';
        // Scheduling is optional, but half of it is not a date.
        if (Boolean(form.scheduledDate) !== Boolean(form.scheduledTime)) {
            errs.scheduled = 'Indica fecha y hora, o deja ambas vacías';
        }
        return errs;
    };

    // Both halves or neither — validate() rejects a lone date or time.
    const scheduledFor = form.scheduledDate && form.scheduledTime
        ? new Date(`${form.scheduledDate}T${form.scheduledTime}`)
        : null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        // When the visit is dated, and what that makes it:
        //  · a date was picked              → booked for that moment (programada)
        //  · none, and the visit is under
        //    way or closed                  → leave it dated exactly as it was
        //  · none, new visit or a booking
        //    whose date was just removed    → happening right now (en curso)
        // A visit that already closed keeps its outcome either way: rescheduling
        // a completed exam must not reopen it.
        const closed = editingAppointment
            && (editingAppointment.status === APPOINTMENT_STATUS.COMPLETED
                || editingAppointment.status === APPOINTMENT_STATUS.CANCELLED);

        let appointmentDate;
        let status;
        if (scheduledFor) {
            appointmentDate = scheduledFor.toISOString();
            status = closed ? editingAppointment.status : APPOINTMENT_STATUS.SCHEDULED;
        } else if (editingAppointment && editingAppointment.status !== APPOINTMENT_STATUS.SCHEDULED) {
            appointmentDate = editingAppointment.appointmentDate;
            status = editingAppointment.status;
        } else {
            appointmentDate = new Date().toISOString();
            status = APPOINTMENT_STATUS.IN_PROGRESS;
        }

        const base = {
            cause:    form.cause.trim(),
            vetNotes: form.vetNotes.trim(),
            appointmentDate,
            status,
        };

        if (mode === 'walkin') {
            onSubmit({
                ...base,
                isWalkIn: true,
                walkInInfo: {
                    fullName: form.fullName.trim(),
                    phone:    form.phone.trim(),
                    dogName:  form.dogName.trim(),
                    address:  form.address.trim(),
                },
            });
        } else {
            onSubmit({ ...base, isWalkIn: false, dogId: form.dogId });
        }
    };

    const fmtDateTime = (iso) => {
        if (!iso) return '';
        return new Date(iso).toLocaleString('es-ES', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const createdBy  = editingAppointment?.createdBy?.username  || editingAppointment?.createdBy?.name;
    const updatedBy  = editingAppointment?.updatedBy?.username  || editingAppointment?.updatedBy?.name;
    const wasEdited  = updatedBy && updatedBy !== createdBy;

    return (
        <div className="wi-wrap">
            <div className="wi-mode-toggle">
                <button
                    type="button"
                    className={`wi-mode-btn${mode === 'registered' ? ' active' : ''}`}
                    onClick={() => { setMode('registered'); setErrors({}); }}
                >
                    🐕 Perro registrado
                </button>
                <button
                    type="button"
                    className={`wi-mode-btn${mode === 'walkin' ? ' active' : ''}`}
                    onClick={() => { setMode('walkin'); setErrors({}); }}
                >
                    🚶 Walk-in
                </button>
            </div>

            <form onSubmit={handleSubmit} className="wi-form">
                {mode === 'walkin' ? (
                    <div className="wi-section">
                        <div className="wi-section-title">Información del visitante</div>
                        <div className="wi-field">
                            <label>Nombre completo *</label>
                            <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Juan García" />
                            {errors.fullName && <span className="wi-error">{errors.fullName}</span>}
                        </div>
                        <div className="wi-field">
                            <label>Teléfono *</label>
                            <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+52 55 1234 5678" />
                            {errors.phone && <span className="wi-error">{errors.phone}</span>}
                        </div>
                        <div className="wi-field">
                            <label>Nombre del perro *</label>
                            <input name="dogName" value={form.dogName} onChange={handleChange} placeholder="Max" />
                            {errors.dogName && <span className="wi-error">{errors.dogName}</span>}
                        </div>
                        <div className="wi-field">
                            <label>Dirección <span className="wi-optional">(opcional)</span></label>
                            <input name="address" value={form.address} onChange={handleChange} placeholder="Calle, colonia, ciudad…" />
                        </div>
                    </div>
                ) : (
                    <div className="wi-section">
                        <div className="wi-section-title">Seleccionar perro registrado</div>
                        <div className="wi-dog-search-wrap">
                            <input
                                className="wi-dog-search"
                                type="text"
                                placeholder="🔍 Buscar por nombre, raza o dueño…"
                                value={dogSearch}
                                onChange={e => setDogSearch(e.target.value)}
                            />
                            {dogSearch && (
                                <button type="button" className="wi-dog-search-clear" onClick={() => setDogSearch('')}>✕</button>
                            )}
                        </div>
                        <div className="wi-dog-list">
                            {dogs.map(dog => (
                                <button
                                    type="button"
                                    key={dog._id}
                                    className={`wi-dog-item${form.dogId === dog._id ? ' selected' : ''}`}
                                    onClick={() => { setForm(prev => ({ ...prev, dogId: dog._id })); setErrors(prev => ({ ...prev, dogId: '' })); }}
                                >
                                    <div className="wi-dog-avatar">
                                        {dog.pictureUrl || dog.picture
                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                            : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                        }
                                    </div>
                                    <div className="wi-dog-info">
                                        <div className="wi-dog-name">{dog.name}</div>
                                        <div className="wi-dog-breed">{dog.breed}{dog.owners?.[0] ? ` · ${dog.owners[0].name}` : ''}</div>
                                    </div>
                                </button>
                            ))}
                            {dogs.length === 0 && (
                                <div className="wi-dog-empty">No se encontraron perros</div>
                            )}
                        </div>
                        {errors.dogId && <span className="wi-error">{errors.dogId}</span>}
                    </div>
                )}

                <div className="wi-section">
                    <div className="wi-section-title">Visita</div>
                    <div className="wi-field">
                        <label>Motivo de visita *</label>
                        <textarea
                            name="cause"
                            value={form.cause}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Describe el motivo principal de la visita…"
                        />
                        {errors.cause && <span className="wi-error">{errors.cause}</span>}
                    </div>
                    <div className="wi-field">
                        <label>Notas para el veterinario <span className="wi-optional">(opcional)</span></label>
                        <textarea
                            name="vetNotes"
                            value={form.vetNotes}
                            onChange={handleChange}
                            rows={2}
                            placeholder="Síntomas, comportamiento, medicamentos actuales…"
                        />
                    </div>
                    <div className="wi-field">
                        <div className="wi-label-row">
                            <label htmlFor="wi-scheduled-date">
                                Programar para <span className="wi-optional">(opcional)</span>
                            </label>
                            {(form.scheduledDate || form.scheduledTime) && (
                                <button
                                    type="button"
                                    className="wi-clear-sched"
                                    onClick={() => {
                                        setForm(prev => ({ ...prev, scheduledDate: '', scheduledTime: '' }));
                                        setErrors(prev => ({ ...prev, scheduled: '' }));
                                    }}
                                >
                                    Quitar
                                </button>
                            )}
                        </div>
                        <div className="wi-field-row">
                            <input
                                id="wi-scheduled-date"
                                name="scheduledDate"
                                type="date"
                                value={form.scheduledDate}
                                onChange={e => { handleChange(e); setErrors(prev => ({ ...prev, scheduled: '' })); }}
                            />
                            <input
                                name="scheduledTime"
                                type="time"
                                value={form.scheduledTime}
                                onChange={e => { handleChange(e); setErrors(prev => ({ ...prev, scheduled: '' })); }}
                            />
                        </div>
                        <span className="wi-hint">
                            {scheduledFor
                                ? `Quedará programada para el ${fmtDateTime(scheduledFor.toISOString())}.`
                                : 'Déjalo vacío si la visita es ahora mismo.'}
                        </span>
                        {errors.scheduled && <span className="wi-error">{errors.scheduled}</span>}
                    </div>
                </div>

                {editingAppointment && (
                    <div className="wi-audit">
                        {createdBy && (
                            <div className="wi-audit-row">
                                <span>Creado por</span>
                                <strong>{createdBy}</strong>
                                <span className="wi-audit-date">{fmtDateTime(editingAppointment.createdAt)}</span>
                            </div>
                        )}
                        {wasEdited && (
                            <div className="wi-audit-row">
                                <span>Editado por</span>
                                <strong>{updatedBy}</strong>
                                <span className="wi-audit-date">{fmtDateTime(editingAppointment.updatedAt)}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="wi-actions">
                    <button type="button" className="wi-btn-cancel" onClick={onCancel}>Cancelar</button>
                    <button type="submit" className="wi-btn-save" disabled={loading}>
                        {loading
                            ? 'Guardando…'
                            : editingAppointment
                                ? 'Actualizar'
                                : scheduledFor ? 'Programar visita' : 'Registrar visita'}
                    </button>
                </div>
            </form>
        </div>
    );
}
